import { type ReactEventHandler, forwardRef } from "react";
import { Box, HtImage } from "@hightouch/htui";
import type {
  MediaItem,
  VideoSource,
} from "src/pages/ad-studio/insights/performance/types";
import { YouTubeEmbed } from "src/pages/ad-studio/insights/youtube-embed";

type MediaEvents = {
  onLoad?: ReactEventHandler<HTMLElement>;
  onLoadedMetadata?: ReactEventHandler<HTMLElement>;
  onError?: ReactEventHandler<HTMLElement>;
};

type AdMediaItemProps = MediaEvents & { item: MediaItem; title: string };

const DrilldownVideo = forwardRef<
  HTMLImageElement & HTMLVideoElement,
  MediaEvents & { video: VideoSource }
>(({ video, onLoadedMetadata, onError }, ref) => {
  switch (video.kind) {
    case "youtube": {
      return (
        <Box width="100%" maxWidth="3xl">
          <YouTubeEmbed
            videoId={video.videoId}
            thumbnailUrl={video.thumbnailUrl}
            aspectRatio="16 / 9"
            borderRadius="lg"
          />
        </Box>
      );
    }
    case "file": {
      return (
        <Box
          ref={ref}
          as="video"
          src={video.url}
          onLoadedMetadata={onLoadedMetadata}
          onError={onError}
          controls
          muted
          display="block"
          maxWidth="100%"
          maxHeight="100%"
          objectFit="contain"
        />
      );
    }
    default: {
      return video satisfies never;
    }
  }
});

export const AdMediaItem = forwardRef<
  HTMLImageElement & HTMLVideoElement,
  AdMediaItemProps
>(({ item, title, onLoad, onLoadedMetadata, onError }, ref) => {
  switch (item.kind) {
    case "video": {
      return (
        <DrilldownVideo
          ref={ref}
          video={item.video}
          onLoadedMetadata={onLoadedMetadata}
          onError={onError}
        />
      );
    }
    case "image": {
      return (
        <HtImage
          ref={ref}
          src={item.url}
          onLoad={onLoad}
          onError={onError}
          alt={title}
          display="block"
          maxWidth="100%"
          maxHeight="100%"
          objectFit="contain"
        />
      );
    }
    default: {
      return item satisfies never;
    }
  }
});
